import { useEffect, useMemo, useState } from "react";

import { Flyout } from "@bokio/components/Flyout/Flyout";
import { useNotificationActivity } from "@bokio/contexts/NotificationActivityContext/useNotificationActivity";
import { CloseButton } from "@bokio/designsystem/components/CloseButton/CloseButton";
import { EmptyState } from "@bokio/designsystem/components/EmptyState/EmptyState";
import { LoadingContent } from "@bokio/designsystem/components/Loading";
import { Link } from "@bokio/elements/Link/Link";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { GeneralLangFactory } from "@bokio/lang";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { getRoute } from "@bokio/shared/route";
import { mergeClassNames } from "@bokio/utils/classes";
import { distanceInWords } from "@bokio/utils/time";

import type { FC } from "react";

import * as styles from "./notificationFlyout.scss";

import NotificationStatus = m.Entities.Notifications.NotificationStatusTypes;
import NotificationArea = m.Entities.Notifications.NotificationArea;
type NotificationDto = m.Core.Services.Notifications.Dtos.NotificationDto;
import NotificationItemType = m.Entities.Notifications.NotificationItemType;
export const formatNotifications = (notifications: NotificationDto[]): [NotificationDto[], NotificationDto[]] => {
	const maxRecentItems = 10;
	const maxRecentDays = 7;
	const recentNotifications: NotificationDto[] = [];
	let earlierNotifications: NotificationDto[] = [];
	let recent = 0;
	const now = new Date().getTime();
	for (const notification of notifications) {
		if (recent < maxRecentItems) {
			const timeDifference = now - notification.Timestamp.getTime();
			const dayDifference = timeDifference / (1000 * 3600 * 24);
			if (dayDifference <= maxRecentDays) {
				recent++;
				recentNotifications.push(notification);
			} else {
				break;
			}
		} else {
			break;
		}
	}
	earlierNotifications = notifications.slice(recent);
	return [recentNotifications, earlierNotifications];
};

const NotificationItem: FC<{
	notification: NotificationDto;
	onClick: () => void;
	companyId: string;
	isBackOffice: boolean;
	isUserNotification?: boolean;
	testId?: string;
}> = ({ notification, onClick, companyId, isBackOffice, isUserNotification, testId }) => {
	const [setNotificationsStatuses] = useLazyApi(
		proxy.Notifications.NotificationController.SetNotificationsStatuses.Put,
	);
	const [setUserNotificationsStatuses] = useLazyApi(
		proxy.Notifications.UserNotificationController.SetUserNotificationsStatuses.Put,
	);

	const handleNotificationClick = (notification: NotificationDto) => {
		const notificationIds = [notification.Id];
		if (isUserNotification) {
			setUserNotificationsStatuses(notificationIds, m.Entities.Notifications.NotificationStatusTypes.Read);
		} else {
			setNotificationsStatuses(companyId, notificationIds, m.Entities.Notifications.NotificationStatusTypes.Read);
		}
	};

	const getNotificationRoute = () => {
		if (isUserNotification && notification.Area === NotificationArea.UserSecurityEvent) {
			return getRoute("accountSecurity");
		}
		if (notification.Area === NotificationArea.System) {
			return notification.LinkTo;
		}
		if (isBackOffice) {
			return getRoute("agencyNotificationRedirect", {
				agencyId: companyId,
				area: notification.Area,
				areaType: notification.AreaType,
				itemId: notification.ItemId,
				itemType: notification.ItemType,
				linkTo: notification.LinkTo,
			});
		}
		return getRoute("notificationRedirect", {
			company: companyId,
			area: notification.Area,
			areaType: notification.AreaType,
			itemId: notification.ItemId,
			itemType: notification.ItemType,
		});
	};

	return (
		<Link
			style="none"
			className={mergeClassNames(
				styles.notificationItem,
				notification.Status === NotificationStatus.Read && styles.notificationItemRead,
			)}
			data-testid="NotificationItem"
			onClick={e => {
				if (notification.ItemType === NotificationItemType.MentionComment && !isBackOffice && !isUserNotification) {
					handleNotificationClick(notification);
					e.preventDefault();
				}
				onClick();
			}}
			route={getNotificationRoute()}
			testId={testId}
		>
			<Markdown
				markdownContent={notification.Message}
				deprecatedNativeHtmlElementClassName={styles.notificationItemTitle}
				deprecatedOptoutFromTypographicElements
			/>
			<p className={styles.notificationItemCaption}>{distanceInWords(notification.Timestamp)}</p>
		</Link>
	);
};

interface NotificationFlyoutProps {
	companyId: string;
	isOpen: boolean;
	onClose: () => void;
	isBackOffice: boolean;
}

export const NotificationFlyout = ({ companyId, isOpen, onClose, isBackOffice }: NotificationFlyoutProps) => {
	const generalLang = GeneralLangFactory();
	const ping = useNotificationActivity();

	const [setNotificationsStatuses] = useLazyApi(
		isBackOffice
			? proxy.BackOffice.NotificationController.SetNotificationsStatuses.Put
			: proxy.Notifications.NotificationController.SetNotificationsStatuses.Put,
	);

	const [setUserNotificationsStatuses] = useLazyApi(
		proxy.Notifications.UserNotificationController.SetUserNotificationsStatuses.Put,
	);

	const [userNotificationIds, setUserNotificationIds] = useState(() => new Set<string>());

	const [getNotifications, getNotificationsRequest] = useLazyApi(
		isBackOffice
			? proxy.BackOffice.NotificationController.GetNotificationsForUser.Get
			: proxy.Notifications.NotificationController.GetNotificationsForUser.Get,

		{
			onSuccess: data => {
				const newNotificationIds = data.filter(n => n.Status === NotificationStatus.New).map(n => n.Id);
				if (newNotificationIds.length > 0) {
					setNotificationsStatuses(companyId, newNotificationIds, NotificationStatus.Seen);
				}
			},
		},
	);

	const [getUserNotifications, getUserNotificationsRequest] = useLazyApi(
		proxy.Notifications.UserNotificationController.GetUserNotifications.Get,
		{
			onSuccess: data => {
				setUserNotificationIds(new Set(data.map(n => n.Id)));
				const newNotificationIds = data.filter(n => n.Status === NotificationStatus.New).map(n => n.Id);
				if (newNotificationIds.length > 0) {
					setUserNotificationsStatuses(newNotificationIds, NotificationStatus.Seen);
				}
			},
		},
	);

	useEffect(() => {
		if (isOpen) {
			getNotifications(companyId);
			getUserNotifications();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ping, isOpen, companyId]);

	const mergedNotifications = useMemo(() => {
		const company = getNotificationsRequest.data?.Data ?? [];
		const user = getUserNotificationsRequest.data?.Data ?? [];
		return [...company, ...user].sort((a, b) => b.Timestamp.getTime() - a.Timestamp.getTime());
	}, [getNotificationsRequest.data, getUserNotificationsRequest.data]);

	const isLoading =
		getNotificationsRequest.isLoading ||
		getUserNotificationsRequest.isLoading ||
		(!getNotificationsRequest.data && !getUserNotificationsRequest.data);

	const [localReadNotifications, setLocalReadNotifications] = useState(() => new Set<string>());

	const readNotifications = (notificationIds: string[] = []) => {
		setLocalReadNotifications(set => new Set([...set, ...notificationIds]));
		const companyIds = notificationIds.filter(id => !userNotificationIds.has(id));
		const userIds = notificationIds.filter(id => userNotificationIds.has(id));
		if (companyIds.length > 0) {
			setNotificationsStatuses(companyId, companyIds, m.Entities.Notifications.NotificationStatusTypes.Read);
		}
		if (userIds.length > 0) {
			setUserNotificationsStatuses(userIds, m.Entities.Notifications.NotificationStatusTypes.Read);
		}
	};

	const handleNotificationClick = (notification: NotificationDto) => {
		if (notification.Status !== NotificationStatus.Read) {
			readNotifications([notification.Id]);
		}
		onClose();
	};

	const renderNotificationItem = (notification: NotificationDto) => (
		<NotificationItem
			notification={{
				...notification,
				Status: localReadNotifications.has(notification.Id) ? NotificationStatus.Read : notification.Status,
			}}
			key={notification.Id}
			onClick={() => handleNotificationClick(notification)}
			companyId={companyId}
			isBackOffice={isBackOffice}
			isUserNotification={userNotificationIds.has(notification.Id)}
			testId={`NotificationItem_${notification.Id}`}
		/>
	);

	return (
		<Flyout
			asideClassName={styles.flyoutAside}
			backgroundClassName={styles.flyoutBackground}
			isOpen={isOpen}
			onClickOutside={onClose}
		>
			<div className={styles.header}>
				<div className={!isBackOffice ? styles.closeButtonContainer : ""}>
					<CloseButton onClick={onClose} />
				</div>
				<h3 className={styles.headerText}>{generalLang.NotificationFlyout_Notifications}</h3>
				{companyId && !!isBackOffice && (
					<Link
						className={styles.manage}
						route={getRoute("agencyNotificationSettings", { agencyId: companyId })}
						onClick={onClose}
					>
						{generalLang.Manage}
					</Link>
				)}
				{companyId && !isBackOffice && (
					<Link
						className={styles.manage}
						route={getRoute("notificationSettings", { company: companyId })}
						onClick={onClose}
					>
						{generalLang.Manage}
					</Link>
				)}
			</div>
			{isLoading ? (
				<LoadingContent />
			) : (
				(() => {
					const unreadCount = mergedNotifications.filter(
						n => n.Status !== NotificationStatus.Read && !localReadNotifications.has(n.Id),
					).length;
					const [recentNotifications, earlierNotifications] = formatNotifications(mergedNotifications);
					return (
						<>
							{unreadCount > 0 && (
								<div className={styles.header}>
									<p className={styles.unreadText}>
										<strong className={styles.regularText}>{unreadCount}</strong>{" "}
										{unreadCount === 1
											? generalLang.NotificationFlyout_UnreadNotifications_S
											: generalLang.NotificationFlyout_UnreadNotifications_P}
									</p>
									<Link
										onClick={() =>
											readNotifications(
												mergedNotifications.filter(n => n.Status !== NotificationStatus.Read).map(n => n.Id),
											)
										}
									>
										{generalLang.NotificationFlyout_MarkAllAsRead}
									</Link>
								</div>
							)}
							{recentNotifications.length > 0 || earlierNotifications.length > 0 ? (
								<div className={styles.scrollable}>
									{recentNotifications.length > 0 && (
										<>
											<div className={styles.sectionHeader}>{generalLang.NotificationFlyout_Recent}</div>
											{recentNotifications.map(renderNotificationItem)}
										</>
									)}
									{earlierNotifications.length > 0 && (
										<>
											<div className={styles.sectionHeader}>{generalLang.NotificationFlyout_Earlier}</div>
											{earlierNotifications.map(renderNotificationItem)}
										</>
									)}
								</div>
							) : (
								<div className={styles.emptyState}>
									<EmptyState
										graphic="noNotifications"
										title={generalLang.NotificationsEmptyState_Title}
										description={generalLang.NotificationsEmptyState_Content}
									/>
								</div>
							)}
						</>
					);
				})()
			)}
		</Flyout>
	);
};
